import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  GetApp as ExportIcon,
  LocalShipping as ShippingIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchPackingListsStart,
  fetchDropdownsStart,
  deletePackingListStart,
  fetchDetailsStart,
  setSearchTerm,
  setActiveFilter,
  setPage,
  setPageSize,
  clearError,
  generateReportStart,
} from '../../store/reducers/packingListSlice';
import { PackingList } from '../../services/packingListApi';
import { PDFExportService } from '../../services/pdfExportService';

/**
 * PackingListList component that displays a list of packing lists with filtering,
 * search, and management capabilities
 */
const PackingListList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    packingLists,
    loading,
    error,
    currentPage,
    pageSize,
    totalCount,
    searchTerm,
    activeFilter,
    loadingDropdowns,
    selectedDetails,
  } = useSelector((state: RootState) => state.packingList);

  // Local state
  const [selectedPackingList, setSelectedPackingList] = useState<PackingList | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchPackingListsStart());
    dispatch(fetchDropdownsStart());
  }, [dispatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    dispatch(setActiveFilter(value === '' ? null : value === 'true'));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1)); // Material-UI is 0-based, our API is 1-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRefresh = () => {
    dispatch(fetchPackingListsStart());
  };

  const handleCreatePackingList = () => {
    navigate('/packing-lists/add');
  };

  const handleViewPackingList = (packingList: PackingList) => {
    navigate(`/packing-lists/edit/${packingList.id}`);
  };

  const handleEditPackingList = (packingList: PackingList) => {
    navigate(`/packing-lists/edit/${packingList.id}`);
  };

  const handleDeletePackingList = (packingList: PackingList) => {
    setSelectedPackingList(packingList);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedPackingList) {
      dispatch(deletePackingListStart(selectedPackingList.id!));
    }
    setDeleteConfirmOpen(false);
    setSelectedPackingList(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSelectedPackingList(null);
  };

  const handlePrintPackingList = (packingList: PackingList) => {
    setSelectedPackingList(packingList);
    setExportDialogOpen(true);
    
    // Also fetch details for this packing list
    if (packingList.id) {
      dispatch(fetchDetailsStart(packingList.id));
    }
  };

  const handleExportPackingList = (packingList: PackingList) => {
    // Use the same print dialog for export
    handlePrintPackingList(packingList);
  };

  const handleExportOptionSelect = async (exportType: 'simple' | 'professional') => {
    if (!selectedPackingList) return;

    setExportingPdf(true);
    setExportError(null);

    try {
      const printData = {
        packingList: selectedPackingList,
        details: selectedDetails,
        companyLogo: '/images/otm-lo.png' // Match Angular implementation
      };

      if (exportType === 'simple') {
        PDFExportService.generateSimplePDF(printData);
      } else {
        await PDFExportService.downloadProfessionalPDF(printData);
      }

      setExportDialogOpen(false);
      setSelectedPackingList(null);
    } catch (error: any) {
      setExportError(error.message || 'Failed to generate PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setSelectedPackingList(null);
    setExportError(null);
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const getStatusColor = (active: boolean): 'default' | 'primary' | 'secondary' => {
    return active ? 'primary' : 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatShippingAddress = (packingList: PackingList) => {
    const parts = [
      packingList.ShipTo,
      packingList.ShipToCo,
      packingList.ShipToCity,
      packingList.ShipToState
    ].filter(Boolean);
    return parts.join(', ');
  };

  const exportOptions = PDFExportService.getExportOptions();

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading Packing Lists...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Packing Lists
        </Typography>
        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreatePackingList}
          >
            Add New Packing List
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Packing Lists"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter === null ? '' : activeFilter.toString()}
                onChange={handleActiveFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />

      {/* Packing Lists Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Packing List Number</TableCell>
              <TableCell>Packing List Name</TableCell>
              <TableCell>Customer PO</TableCell>
              <TableCell>Ship To</TableCell>
              <TableCell>Total Weight</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={30} />
                  <Typography variant="body2" style={{ marginLeft: '1rem' }}>
                    Loading packing lists...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : packingLists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No packing lists found. {searchTerm && `Try adjusting your search term.`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              packingLists.map((packingList) => (
                <TableRow key={packingList.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {packingList.PackingListNum}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {packingList.RevDate && formatDate(packingList.RevDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {packingList.PackingListName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {packingList.Number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatShippingAddress(packingList)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {packingList.ShipToZIP}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {packingList.NetWeight ? `${packingList.NetWeight} lb` : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Gross: {packingList.GrossWeight ? `${packingList.GrossWeight} lb` : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={packingList.Active ? 'Active' : 'Inactive'}
                      color={getStatusColor(packingList.Active)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" style={{ gap: '4px' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewPackingList(packingList)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditPackingList(packingList)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePackingList(packingList)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print/PDF">
                        <IconButton
                          size="small"
                          onClick={() => handlePrintPackingList(packingList)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export Options">
                        <IconButton
                          size="small"
                          onClick={() => handleExportPackingList(packingList)}
                        >
                          <ExportIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1} // Material-UI is 0-based, our API is 1-based
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Packing lists per page:"
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Packing List</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the packing list "{selectedPackingList?.PackingListNum}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Export Options Dialog */}
      <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon style={{ marginRight: '8px' }} />
            Export Packing List: {selectedPackingList?.PackingListNum}
          </Box>
        </DialogTitle>
        <DialogContent>
          {exportError ? (
            <Box mb={2}>
              <Typography color="error" variant="body2">
                {exportError}
              </Typography>
            </Box>
          ) : null}
          
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '1rem' }}>
            Choose your preferred export method:
          </Typography>
          
          <List>
            {exportOptions.map((option) => (
              <ListItem
                key={option.id}
                button
                disabled={!option.available || exportingPdf}
                onClick={() => handleExportOptionSelect(option.id as 'simple' | 'professional')}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  backgroundColor: option.available ? 'inherit' : '#f5f5f5'
                }}
              >
                <ListItemIcon>
                  {option.id === 'simple' ? <PrintIcon /> : <PdfIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={option.name}
                  secondary={option.description + (option.available ? '' : ' (Not available)')}
                />
              </ListItem>
            ))}
          </List>

          {exportingPdf && (
            <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
              <CircularProgress size={20} style={{ marginRight: '8px' }} />
              <Typography variant="body2">Generating PDF...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={exportingPdf}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackingListList; 